import React, { useState, useEffect } from 'react';
import { Modal, Button, Spin, Alert, Typography, Space, Tag } from 'antd';
import { DownloadOutlined, ExportOutlined } from '@ant-design/icons';
import { fileManagementApi } from '../lib/fileManagementApi';
import { formatBytes, getFileIcon, isPreviewableFile } from '../utils/fileUtils';

const { Text, Title } = Typography;

interface FilePreviewProps {
  visible: boolean;
  onClose: () => void;
  file: {
    key: string;
    bucket: string;
    filename: string;
    contentType: string;
    size: number;
    uploadedAt: string;
    metadata?: Record<string, any>;
  } | null;
}

const FilePreview: React.FC<FilePreviewProps> = ({ visible, onClose, file }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && file) {
      loadPreview();
    } else {
      setPreviewUrl(null);
      setError(null);
    }
  }, [visible, file]);

  const loadPreview = async () => {
    if (!file) return;

    try {
      setLoading(true);
      setError(null);

      if (isPreviewableFile(file.contentType)) {
        // Generate authenticated URL for preview
        const url = fileManagementApi.getAuthenticatedFileUrl(file.bucket, file.key);
        setPreviewUrl(url);
      }
    } catch (err) {
      setError('Failed to load file preview');
      console.error('Preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!file) return;

    try {
      const response = await fileManagementApi.downloadFile(file.bucket, file.key);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleOpenNewTab = () => {
    if (!file) return;
    
    // For images and other previewable files, create a better URL for new tab
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Fallback: direct file URL
      const fileUrl = fileManagementApi.getAuthenticatedFileUrl(file.bucket, file.key);
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Loading preview...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          message="Preview Error"
          description={error}
          type="error"
          showIcon
          style={{ margin: '20px 0' }}
        />
      );
    }

    if (!file) return null;

    const { contentType } = file;

    // Image preview
    if (contentType.startsWith('image/')) {
      return (
        <div style={{ textAlign: 'center' }}>
          <img
            src={previewUrl || ''}
            alt={file.filename}
            style={{
              maxWidth: '100%',
              maxHeight: '500px',
              objectFit: 'contain',
              border: '1px solid #f0f0f0',
              borderRadius: '4px',
            }}
            onError={() => setError('Failed to load image')}
          />
        </div>
      );
    }

    // PDF preview
    if (contentType === 'application/pdf') {
      return (
        <div style={{ height: '500px' }}>
          <embed
            src={previewUrl || ''}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </div>
      );
    }

    // Text/Code preview
    if (contentType.startsWith('text/') || contentType === 'application/json') {
      return (
        <div style={{ height: '500px' }}>
          <iframe
            src={previewUrl || ''}
            width="100%"
            height="100%"
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
            }}
          />
        </div>
      );
    }

    // Video preview
    if (contentType.startsWith('video/')) {
      return (
        <div style={{ textAlign: 'center' }}>
          <video
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '500px',
            }}
          >
            <source src={previewUrl || ''} type={contentType} />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // Audio preview
    if (contentType.startsWith('audio/')) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <audio controls style={{ width: '100%' }}>
            <source src={previewUrl || ''} type={contentType} />
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    // Default preview for unsupported types
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        {getFileIcon(contentType, { fontSize: '64px' })}
        <Title level={4} style={{ marginTop: '16px' }}>
          {file.filename}
        </Title>
        <Space direction="vertical" size="small">
          <Text type="secondary">Type: {contentType}</Text>
          <Text type="secondary">Size: {formatBytes(file.size)}</Text>
          <Text type="secondary">
            Uploaded: {new Date(file.uploadedAt).toLocaleString()}
          </Text>
          {file.metadata?.projectName && (
            <Tag color="blue">Project: {file.metadata.projectName}</Tag>
          )}
        </Space>
        <p style={{ marginTop: '20px', color: '#666' }}>
          Preview not available for this file type.<br />
          Use the download button to view the file.
        </p>
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <span>File Preview</span>
          {file && (
            <Tag color="blue">{file.filename}</Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="download" icon={<DownloadOutlined />} onClick={handleDownload}>
          Download
        </Button>,
        ...(file && (previewUrl || file.contentType.startsWith('image/') || file.contentType === 'application/pdf' || file.contentType.startsWith('text/')) ? [
          <Button
            key="newTab"
            type="primary"
            icon={<ExportOutlined />}
            onClick={handleOpenNewTab}
          >
            Open in New Tab
          </Button>
        ] : []),
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      styles={{
        body: { padding: '16px' }
      }}
    >
      {renderPreview()}
    </Modal>
  );
};

export default FilePreview;